// @ts-nocheck
import { ComponentProps, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useTable } from "react-table";
import Paginator from './Paginator'
import { Column } from 'react-table'
import { useDatatableContext } from "./DatatableContext";
import { useParams } from 'react-router-dom'
import { useIntl } from "react-intl";
import clsx from "clsx";


type Props<T extends object> = {
    data: any
    columns: Column<T>[]
    selectable?: boolean;
    onClick?: (row) => void
    bullet?: JSX.Element
    headerStyle?: React.CSSProperties
    className?: string
} & ComponentProps<'table'>

export default function SimpleDatatable<T extends object>(props: Props<T>) {
    const {
        data,
        columns,
        selectable,
        onClick,
        bullet,
        headerStyle,
        className,
    } = props
    // ensure that data isn't recreated on every render
    const tableData = useMemo(() => data, [data])
    const tableColumns = useMemo(() => columns, [columns])
    const { datatableContext, setDatatableContext } = useDatatableContext() || {
        datatableContext: [],
        setDatatableContext: () => { },
    };
    const params = useParams();
    const path = params['*']
    const intl = useIntl()

    useEffect(() => {
        if (selectable && setDatatableContext) {
            setDatatableContext([]);
        }
    }, [path]);

    const isAllSelected = useMemo(() => {
        if (!selectable || tableData.length === 0) return false;
        return tableData.every((item) => datatableContext.includes(item));
    }, [tableData, datatableContext, selectable]);

    const tableInstance = useTable(
        {
            columns: tableColumns,
            data: tableData,
            autoResetSelectedRows: false,
            datatableContext,
            setDatatableContext,
            isAllSelected,
            // getRowId: (row) => row._id,
        },
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                ...(bullet
                    ? [
                        {
                            id: 'bullet',
                            Header: () => null,
                            Cell: ({ row }) => (
                                <span style={{ padding: '0 5px', display: 'inline-block' }}>
                                    {bullet}
                                </span>
                            ),
                            width: 40,
                            minWidth: 40,
                        },
                    ]
                    : []),
                ...(selectable
                    ? [
                        {
                            id: 'selection',
                            Header: ({ rows, datatableContext, setDatatableContext, isAllSelected }) => {
                                const toggleAll = (status: boolean) => {
                                    if (status) {
                                        const newSelections = [...datatableContext];
                                        rows.forEach((row) => {
                                            if (!newSelections.includes(row.original)) {
                                                newSelections.push(row.original);
                                            }
                                        });
                                        setDatatableContext(newSelections);
                                    } else {
                                        setDatatableContext((prev) =>
                                            prev.filter((selectedRow) => !rows.find((r) => r.original === selectedRow))
                                        );
                                    }
                                };

                                return (
                                    <div>
                                        <input
                                            className='form-check-input form-check-sm'
                                            type='checkbox'
                                            onChange={(e) => toggleAll(e.target.checked)}
                                            checked={isAllSelected}
                                        />
                                    </div>
                                );
                            },
                            Cell: ({ row, datatableContext, setDatatableContext }) => {
                                const handleRowSelection = (row) => {
                                    setDatatableContext((prevSelectedRow) => {
                                        if (prevSelectedRow.includes(row.original)) {
                                            return prevSelectedRow.filter((selectedRow) => selectedRow !== row.original);
                                        } else {
                                            return [...prevSelectedRow, row.original];
                                        }
                                    });
                                };

                                return (
                                    <div className='d-flex' onClick={(e) => e.stopPropagation()}>
                                        <input
                                            className='form-check-input form-check-sm'
                                            type='checkbox'
                                            onChange={() => handleRowSelection(row)}
                                            checked={datatableContext.includes(row.original)}
                                        />
                                    </div>
                                );
                            },
                            minWidth: 50,
                        },
                    ]
                    : []),
                ...columns,
            ]);
        },
    );

    const {
        getTableProps, // Required
        getTableBodyProps, // Required
        headerGroups,
        rows,
        prepareRow, // Required
    } = tableInstance

    return (
        // apply the table props
        <div className='overflow-auto'>
            <table
                className={clsx('table table-rounded gy-2 gs-2', className)}
                {...getTableProps()}
            >
                <thead className=''>
                    {
                        // Loop over the header rows
                        headerGroups.map((headerGroup) => (
                            // Apply the header row props
                            <tr
                                className='fw-bolder fs-7 text-gray-800 border-bottom border-gray-200'
                                {...headerGroup.getHeaderGroupProps()}
                            >
                                {
                                    // Loop over the headers in each row
                                    headerGroup.headers.map((column) => {
                                        const headerProps = column.getHeaderProps({
                                            style: { minWidth: column.minWidth },
                                        });

                                        const finalStyle = {
                                            ...headerProps.style,
                                            ...(headerStyle ? headerStyle : {}),
                                        };
                                        return (
                                            // Apply the header cell props
                                            <th {...headerProps} style={finalStyle}>
                                                {
                                                    // Render the header
                                                    column.render('Header')
                                                }
                                            </th>
                                        )
                                    })
                                }
                            </tr>
                        ))
                    }
                </thead>
                {/* Apply the table body props */}
                <tbody className='' {...getTableBodyProps()}>
                    {
                        // Loop over the table rows
                        rows.map((row) => {
                            // Prepare the row for display
                            prepareRow(row)
                            return (
                                // Apply the row props
                                <tr className="border-bottom border-light" {...row.getRowProps()}>
                                    {
                                        // Loop over the rows cells
                                        row.cells.map((cell) => {
                                            // Apply the cell props
                                            return (
                                                <td className="text-truncate fs-7" {...cell.getCellProps()} onClick={() => { onClick?.(row); }}>
                                                    {
                                                        // Render the cell contents
                                                        cell.render('Cell')
                                                    }
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                            )
                        })
                    }
                </tbody>
            </table>

            {data.length === 0 && <div className='text-center'>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })} </div>}
        </div>
    )
}